import { isLoggedIn } from '../auth'
import { SET_AUTH_DATA } from '../auth/redux/actions'
import { createViewRecords, findView } from '../data'
import { SET_LOCATION_DATA } from '../location/redux/actions'
import { isPageReady } from '../pages'
import { SET_PAGE_READY } from '../pages/redux/actions'
import {
  GET_DATA_LIST,
  getDataList as getDataListAction,
  setDataList as setDataListAction,
  PERSIST_DATA,
} from '../data/redux/actions'
import { dismissModal as dismissModalAction } from '../modal/redux/actions'
import { getConfigDataServer } from '../config'
import { showErrorToast, showSuccessToast as showSuccessToastAction } from '../toast/redux/actions'
import { getFirstErrorMessage } from '../error'
import {
  createGiftDetailViewOptions,
  createGiftListViewOptions,
  getGift,
  getGiftList,
  GIFT_STORE_KEY,
  isGiftDetailPage,
  isGiftListPage,
  redeemGift,
  updateGift,
} from '.'

/**
 * Returns true if the action has a storeKey of 'gift'
 * @param {Object} options The parameters
 * @param {Object} [options.action={}] A redux action
 * @param {Object} [options.action.payload] The action payload
 * @param {String} [options.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals content
 */
function isGiftDataAction (options = {}) {
  const { action = {} } = options
  const { payload: { storeKey } = {} } = action
  return storeKey === GIFT_STORE_KEY
}

export const initGiftListPage = ({ after }) => (
  after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ state, prevState, dispatch }) => {
      const { data } = state
      const storeKey = GIFT_STORE_KEY
      const options = createGiftListViewOptions(state)
      const prevOptions = createGiftListViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const contentReady = view && options.equals(prevOptions)

      if (contentReady) {
        return
      }

      dispatch(getDataListAction({
        storeKey,
        options,
      }))
    },
  )
    .when(({ state }) => {
      const { auth, page } = state
      return isLoggedIn(auth) && isPageReady({ page }) && isGiftListPage(state)
    })
)

export const initGiftDetailPage = ({ after }) => (
  after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ state, prevState, dispatch }) => {
      const { data } = state
      const storeKey = GIFT_STORE_KEY
      const options = createGiftDetailViewOptions(state)
      const prevOptions = createGiftDetailViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const viewOptionsChanged = !view || !options.equals(prevOptions)

      if (viewOptionsChanged) {
        dispatch(getDataListAction({
          storeKey,
          options,
        }))
      }
    },
  )
    .when(({ state }) => {
      const { auth, page } = state
      return isLoggedIn(auth) && isPageReady({ page }) && isGiftDetailPage(state)
    })
)

export const getDataListGift = ({ after }) => (
  after(GET_DATA_LIST, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, options } = action.payload

    try {
      let data

      if (options.get('token')) {
        data = await getGift({
          auth,
          server: getConfigDataServer(config, 'auth'),
          token: options.get('token'),
        })
      } else {
        data = await getGiftList({
          auth,
          server: getConfigDataServer(config, 'auth'),
          ...options.toJS(),
        })
      }

      dispatch(setDataListAction({
        storeKey,
        options,
        records: createViewRecords(data.get('gifts'), 'token'),
        total: data.get('total'),
        processing: false,
      }))
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        options,
        processing: false,
        error: e,
      }))
    }
  })
    .when(isGiftDataAction)
)

export const persistDataGift = ({ after }) => (
  after(PERSIST_DATA, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, options } = action.payload
    let { record } = action.payload

    try {
      dispatch(setDataListAction({
        storeKey,
        options,
        processing: true,
      }))

      const isRedeem = options.get('redeem')

      if (isRedeem) {
        record = await redeemGift({
          auth,
          server: getConfigDataServer(config, 'brooklyn'),
          data: record,
        })
        dispatch(dismissModalAction())
        dispatch(showSuccessToastAction('Gift was successfully redeemed'))
      } else {
        record = await updateGift({
          auth,
          server: getConfigDataServer(config, 'auth'),
          data: record,
        })
        dispatch(showSuccessToastAction('Gift was successfully updated'))
      }
      dispatch(setDataListAction({
        storeKey,
        options,
        records: createViewRecords(record.get('gifts'), 'token'),
        total: 1,
        processing: false,
        error: null,
      }))
    } catch (e) {
      dispatch(showErrorToast(getFirstErrorMessage(e)))
      dispatch(setDataListAction({
        storeKey,
        options,
        processing: false,
        error: e,
      }))
    }
  })
    .when(isGiftDataAction)
)
